import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { Box, Stack, Typography } from '@mui/material';
import type { GuideTourTokens } from './guidedTourTheme';

export function GuideStepProgress({
  stepIndex,
  totalSteps,
  tokens,
}: {
  stepIndex: number;
  totalSteps: number;
  tokens: GuideTourTokens;
}) {
  return (
    <>
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, letterSpacing: 0.2, display: 'block', mb: 0.65, color: tokens.accent }}
      >
        Step {stepIndex + 1} of {totalSteps}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mb: 1.15 }} aria-hidden>
        {Array.from({ length: totalSteps }, (_, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              height: 3,
              borderRadius: 999,
              bgcolor: index <= stepIndex ? tokens.progressActive : tokens.progressTrack,
              transition: 'background-color 0.2s ease',
            }}
          />
        ))}
      </Stack>
    </>
  );
}

export function GuideSectionLabel({ children }: { children: string }) {
  return (
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        fontWeight: 700,
        letterSpacing: 0.45,
        lineHeight: 1.2,
        mb: 0.5,
        color: 'text.secondary',
      }}
    >
      {children}
    </Typography>
  );
}

export function GuideBulletList({ items, dotColor }: { items: string[]; dotColor?: string }) {
  if (!items.length) return null;
  return (
    <Stack component="ul" spacing={0.7} sx={{ m: 0, pl: 0, listStyle: 'none' }}>
      {items.map((item) => (
        <Stack key={item} component="li" direction="row" spacing={0.85} alignItems="flex-start">
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: dotColor ?? 'text.primary',
              opacity: 0.85,
              mt: 0.65,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              fontSize: '0.9375rem',
              lineHeight: 1.6,
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: 0.01,
            }}
          >
            {item}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export function GuideIntroText({ children }: { children: string }) {
  return (
    <Typography variant="body2" sx={{ lineHeight: 1.55, fontWeight: 500, color: 'text.secondary', mb: 0.65 }}>
      {children}
    </Typography>
  );
}

export function GuideTipBlock({
  label = 'Tip',
  text,
  points,
  tokens,
}: {
  label?: string;
  text?: string;
  points?: string[];
  tokens: GuideTourTokens;
}) {
  if (!text && !(points?.length)) return null;
  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1.5,
        bgcolor: tokens.tipBg,
        border: '1px solid',
        borderColor: tokens.tipBorder,
      }}
    >
      <Stack direction="row" spacing={0.65} alignItems="center" sx={{ mb: points?.length ? 0.45 : 0.15 }}>
        <LightbulbOutlinedIcon sx={{ fontSize: 16, color: tokens.accent, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ fontWeight: 800, color: tokens.accent }}>
          {label}
        </Typography>
      </Stack>
      {text ? (
        <Typography
          variant="body1"
          sx={{
            fontSize: '0.9375rem',
            lineHeight: 1.6,
            fontWeight: 600,
            color: 'text.primary',
            mb: points?.length ? 0.5 : 0,
          }}
        >
          {text}
        </Typography>
      ) : null}
      {points?.length ? <GuideBulletList items={points} dotColor={tokens.accent} /> : null}
    </Box>
  );
}

export function GuideContentCard({
  label,
  intro,
  points,
  tokens,
}: {
  label: string;
  intro?: string;
  points: string[];
  tokens: GuideTourTokens;
}) {
  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1.75,
        bgcolor: tokens.accentSoft,
        border: '1px solid',
        borderColor: tokens.accentBorder,
        borderLeft: '3px solid',
        borderLeftColor: tokens.accent,
      }}
    >
      <GuideSectionLabel>{label}</GuideSectionLabel>
      {intro ? <GuideIntroText>{intro}</GuideIntroText> : null}
      <GuideBulletList items={points} dotColor={tokens.accent} />
    </Box>
  );
}
